import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import Button from '../ui/Button';
import nurseryService from '../../services/nursery';
import orchardService from '../../services/orchard';
import { slotWhere, compareSlots } from '../../utils/nurseryRowSlots';
import type { NurseryLot, NurseryLocation } from '../../types/nursery';

interface Props {
  lot: NurseryLot;
  locations: NurseryLocation[];
  onClose: () => void;
  onDone: () => void;
}

const placeLabel = (l: NurseryLocation) =>
  l.location_type === 'nursery_row' && l.row_number != null
    ? slotWhere({ ...l, location_name: l.name })
    : [l.farm_name, l.block_name, l.name].filter(Boolean).join(' · ');

/**
 * Says where a lot stands, without moving any trees: the lot keeps its id,
 * count and allocations. For a lot entered with no place, or put down
 * somewhere other than recorded. A farm is enough for picking and despatch,
 * so a holding area with no row can be made here in one step.
 */
function LotPlaceDialog({ lot, locations, onClose, onDone }: Props) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [locationId, setLocationId] = useState<number | ''>('');
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [farmId, setFarmId] = useState<number | ''>(lot.farm_id ?? '');
  const [name, setName] = useState('Holding area');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    orchardService.getAllFarms().then(res => {
      if (!res.success) return;
      const list = (res.data?.farms || []).map(f => ({ id: f.id, name: f.name }));
      setFarms(list);
      if (list.length === 1) setFarmId(id => (id === '' ? list[0].id : id));
    }).catch(() => { /* the farm list stays empty and the form says so */ });
  }, []);

  const options = useMemo(() => locations
    .filter(l => l.id !== lot.location_id)
    .sort((a, b) => compareSlots({ ...a, location_name: a.name }, { ...b, location_name: b.name })),
  [locations, lot.location_id]);

  const save = async () => {
    setError(null);
    if (mode === 'existing' && locationId === '') { setError('Choose a place'); return; }
    if (mode === 'new' && (farmId === '' || !name.trim())) { setError('Give the area a name and a farm'); return; }
    setSaving(true);
    try {
      let target = locationId;
      if (mode === 'new') {
        const made = await nurseryService.createLocation({
          name: name.trim(), location_type: 'container_area', farm_id: Number(farmId),
        });
        if (!made.success || !made.data) {
          setError((made as { error?: { message?: string } }).error?.message || 'Could not make the area');
          return;
        }
        target = made.data.id;
      }
      const res = await nurseryService.updateLot(lot.id, { location_id: Number(target) });
      if (!res.success) {
        setError((res as { error?: { message?: string } }).error?.message || 'Could not set the place');
        return;
      }
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not set the place');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" role="dialog" aria-labelledby="lot-place-title">
        <div className="flex items-center justify-between mb-2">
          <h3 id="lot-place-title" className="text-base font-semibold text-neutral-800">
            {lot.location_id ? 'Change where this lot stands' : 'Set where this lot stands'}
          </h3>
          <button type="button" aria-label="Close" onClick={onClose}><X className="h-5 w-5 text-neutral-400 hover:text-neutral-600" /></button>
        </div>
        <p className="text-sm text-neutral-600 mb-4 break-words">
          Records where the {lot.current_count.toLocaleString()} trees already are. Nothing is moved or split,
          and anything allocated from this lot stays allocated. A farm is enough for picking: a row and
          position are not needed.
        </p>

        <div className="flex flex-wrap gap-4 mb-3 text-sm">
          <label className="flex items-center gap-2">
            <input type="radio" checked={mode === 'existing'} onChange={() => setMode('existing')} />
            A place already set up
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} />
            A new holding area
          </label>
        </div>

        {mode === 'existing' ? (
          <div>
            <label htmlFor="lot-place-select" className="block text-sm font-medium text-neutral-700 mb-1">Place</label>
            <select id="lot-place-select" value={locationId}
              onChange={e => setLocationId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Choose a place…</option>
              {options.map(l => <option key={l.id} value={l.id}>{placeLabel(l)}</option>)}
            </select>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label htmlFor="lot-place-farm" className="block text-sm font-medium text-neutral-700 mb-1">Farm</label>
              <select id="lot-place-farm" value={farmId}
                onChange={e => setFarmId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Choose a farm…</option>
                {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="lot-place-name" className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
              <input id="lot-place-name" value={name} onChange={e => setName(e.target.value)} maxLength={100}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
              <p className="text-xs text-neutral-400 mt-1">
                Holds any tree, like a polyhouse. It stays on Nursery Locations for the next lot.
              </p>
            </div>
          </div>
        )}

        {error && <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  );
}

export default LotPlaceDialog;
